import type { MemoEntry } from "@/lib/types";

/**
 * Was this entry copied in from an EARLIER meeting?
 *
 * `carried_from_entry_id` is stamped by the backend's carry-forward. It is the
 * only durable signal that also works for sub-headings, which carry
 * `log_date = null` — so a date comparison cannot tell a heading typed today from
 * one rolled forward for months.
 *
 * This replaces `isNewInThisMeeting(entry, meetingDate, sessionCreated)`, which
 * guessed from `log_date === meeting_date` plus a set of ids this browser session
 * had created. That set died on reload and the server could not see it, so the
 * lock was a frontend guard-rail only. The same predicate now runs in
 * `memo_access.require_can_edit_history`, which is the actual authorization.
 */
export function isCarriedForward(entry: Pick<MemoEntry, "carried_from_entry_id">): boolean {
  return entry.carried_from_entry_id !== null && entry.carried_from_entry_id !== undefined;
}

/**
 * May this user change the text of — or delete — a carried-forward entry?
 *
 * `can_edit_finalized` is the series' "Bearbeiter für finalisierte Protokolle"
 * ∪ the home workspace's owner/admin ∪ flow-admin. Plain participants may still
 * ADD entries, complete a to-do (with its Lösung), set a Frist, retire, and move —
 * they simply do not rewrite what an earlier meeting recorded.
 */
export function canEditHistory(access: { can_edit_finalized: boolean }): boolean {
  return access.can_edit_finalized;
}

/**
 * Is this entry text-locked for the current user, right now?
 *
 * - Not carried forward → never locked. You may always edit what this meeting wrote.
 * - Carried forward, without the history right → locked in EVERY mode. This is the
 *   change: the lock used to exist only while presenting.
 * - Carried forward, with the right → locked only while presenting, and then only
 *   until the focused unit is unlocked. That ceremony exists so the room sees
 *   history being changed during a screen-share; outside presentation it is noise.
 */
export function isHistoryLocked(opts: {
  entry: Pick<MemoEntry, "carried_from_entry_id">;
  mayEditHistory: boolean;
  presenting: boolean;
  unitUnlocked: boolean;
}): boolean {
  if (!isCarriedForward(opts.entry)) return false;
  if (!opts.mayEditHistory) return true;
  if (!opts.presenting) return false;
  return !opts.unitUnlocked;
}
